import {
  Box,
  Container,
  Flex,
  Heading,
  Image,
  List,
  ListItem,
  Text,
} from "@chakra-ui/react";
import { FaFacebookF, FaTwitter } from "react-icons/fa";
import { useQuery, useQueryClient } from "react-query";
import { Link } from "react-router-dom";
import { useAuth } from "../../../contexts/Auth";
import { getCommunityBookClub } from "../../../services/communityApi";
import { trackEvent } from "../../../shared/analytics";
import usePageMeta from "../../../shared/hooks/usePageMeta";
import Spinner from "../../../shared/components/Spinner";
import { BOOK_CLUB_PATH, buildAuthRedirectPath } from "../../auth/authRedirect";
import "../../homepage/styles/style.css";
import { getBookClubCountdownParts } from "../bookClubCountdown";
import { formatBookClubSchedule, hasBookClubEnded } from "../bookClubSchedule";
import BookClubCountdown from "../components/BookClubCountdown";

const PageShell = ({ children }) => (
  <div className="body-wrap boxed-container">
    <header className="site-header text-light">
      <Container maxW="container.xl">
        <div className="site-header-inner">
          <div className="brand header-brand text-center">
            <Link to="/">
              <Image
                className="header-logo-image"
                src="/images/logo-512.png"
                alt="Redwing"
              />
            </Link>
          </div>
        </div>
      </Container>
    </header>

    <main>{children}</main>

    <footer className="site-footer">
      <div className="site-footer-bottom">
        <Container maxW="container.xl">
          <div className="site-footer-inner">
            <div className="brand footer-brand">
              <Link to="/">
                <Image src="/images/logo-512.png" maxH="64px" alt="Redwing" />
              </Link>
            </div>
            <List
              className="footer-links list-reset"
              display="flex"
              listStyleType="none"
            >
              <ListItem>
                <Link to="/terms">Terms &amp; conditions</Link>
              </ListItem>
              <ListItem>
                <Link to="/privacy">Privacy policy</Link>
              </ListItem>
              <ListItem>
                <Link to="/eula">End User License Agreement</Link>
              </ListItem>
              <ListItem>
                <Link to="/cookie">Cookie policy</Link>
              </ListItem>
              <ListItem>
                <a href="mailto:contact@redwing.app">Contact</a>
              </ListItem>
            </List>
            <ul className="footer-social-links list-reset">
              <li>
                <a href="https://fb.me/theredwingapp">
                  <span className="screen-reader-text">Facebook</span>
                  <FaFacebookF size={24} color="#FFF" />
                </a>
              </li>
              <li>
                <a href="https://twitter.com/TheRedwingApp">
                  <span className="screen-reader-text">Twitter</span>
                  <FaTwitter size={24} color="#FFF" />
                </a>
              </li>
            </ul>
            <div className="footer-copyright">
              &copy; 2023 Redwing, all rights reserved
            </div>
          </div>
        </Container>
      </div>
    </footer>
  </div>
);

const JoinCta = ({ authData, joined }) => {
  const to = authData
    ? BOOK_CLUB_PATH
    : buildAuthRedirectPath("/login", BOOK_CLUB_PATH);
  const label = joined ? "GO TO THE BOOK CLUB" : "JOIN THE BOOK CLUB";

  return (
    <Flex direction="column" align="center" gap="3" textAlign="center">
      <Link
        to={to}
        aria-label={joined ? "Go to the Book Club" : "Join the Book Club"}
        className="button button-primary button-shadow button-wide-mobile book-club-promo-cta"
        onClick={() =>
          trackEvent("book_club_join_click", { placement: "public_page" })
        }
      >
        <span className="book-club-promo-cta-content">{label}</span>
      </Link>
      {!authData && (
        <Text color="gray.300" fontSize={{ base: "sm", md: "md" }}>
          Free to join, no subscription required.{" "}
          <Link
            to={buildAuthRedirectPath("/signup", BOOK_CLUB_PATH)}
            className="public-book-club-signup-link"
          >
            Create a free account
          </Link>
        </Text>
      )}
    </Flex>
  );
};

const PublicBookClub = () => {
  const { authData } = useAuth();
  const queryClient = useQueryClient();
  const query = useQuery("communityBookClub", getCommunityBookClub, {
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  usePageMeta(
    "Redwing Book Club - Read a comic reading order together",
    "Join the Redwing Book Club: a free community read with a curated comic reading order, shared progress and spoiler-safe discussions.",
    "/book-club"
  );

  const club = query.data?.data;
  const isLive =
    !!club && !hasBookClubEnded(club.endsAt) && club.status !== "archived";
  const countdown = club ? getBookClubCountdownParts(club.startsAt) : null;
  const isUpcoming = isLive && club.status === "upcoming" && !!countdown;

  if (query.isLoading) {
    return (
      <PageShell>
        <Spinner />
      </PageShell>
    );
  }

  if (!isLive) {
    return (
      <PageShell>
        <Container
          maxW="container.md"
          py={{ base: 16, md: 24 }}
          textAlign="center"
        >
          <Heading as="h1" color="white" size="xl">
            The next read is being planned
          </Heading>
          <Text mt="4" color="gray.300" fontSize={{ base: "md", md: "lg" }}>
            No Book Club is running right now. Create a free account and
            you&apos;ll be ready to join the moment the next one opens.
          </Text>
          <Flex mt="8" justify="center">
            <JoinCta authData={authData} joined={false} />
          </Flex>
        </Container>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Box
        as="section"
        aria-labelledby="public-book-club-title"
        bg="#0B0D19"
        py={{ base: 12, md: 20 }}
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          inset={0}
          bgImage='url("/images/book-club-energy.jpg")'
          bgPosition={{ base: "75% center", md: "center" }}
          bgSize="cover"
          opacity={{ base: 0.3, md: 0.4 }}
          pointerEvents="none"
          aria-hidden="true"
        />
        <Box
          position="absolute"
          inset={0}
          bgGradient="linear(to-b, rgba(11, 13, 25, 0.96), rgba(11, 13, 25, 0.74) 48%, rgba(11, 13, 25, 0.97))"
          pointerEvents="none"
          aria-hidden="true"
        />

        <Container maxW="container.xl" position="relative" zIndex={1}>
          <Box textAlign="center" mb={{ base: 6, md: 8 }}>
            <Heading
              as="h1"
              id="public-book-club-title"
              color="white"
              fontSize={{ base: "3xl", md: "4xl" }}
              lineHeight="1.05"
              letterSpacing="-0.03em"
            >
              {isUpcoming
                ? "The next Book Club starts soon"
                : "The Book Club is live"}
            </Heading>
            <Text mt="2" color="gray.400" fontSize={{ base: "md", md: "lg" }}>
              {formatBookClubSchedule(club.startsAt, club.endsAt)}
            </Text>
          </Box>

          {isUpcoming && (
            <Box maxW="container.md" mx="auto" mb={{ base: 6, md: 8 }}>
              <BookClubCountdown
                startsAt={club.startsAt}
                onComplete={() =>
                  queryClient.invalidateQueries("communityBookClub")
                }
              />
            </Box>
          )}

          <Box
            className="book-club-promo-card"
            overflow="hidden"
            bg="gray.700"
            borderWidth="1px"
            borderColor="whiteAlpha.200"
            borderRadius="2xl"
            boxShadow="0 18px 45px rgba(0, 0, 0, 0.24)"
          >
            <Flex
              position="relative"
              zIndex={1}
              direction={{ base: "column", md: "row" }}
            >
              <Box
                position="relative"
                flex={{ md: "0 0 35%" }}
                aspectRatio={{ base: "16 / 9", md: "auto" }}
                bg="gray.800"
              >
                {club.cover ? (
                  <Image
                    src={club.cover}
                    alt={`Artwork for ${club.title}`}
                    position="absolute"
                    inset={0}
                    w="100%"
                    h="100%"
                    objectFit="cover"
                  />
                ) : (
                  <Flex
                    position="absolute"
                    inset={0}
                    align="center"
                    justify="center"
                  >
                    <Text fontSize="xs" letterSpacing="0.18em" color="red.200">
                      REDWING BOOK CLUB
                    </Text>
                  </Flex>
                )}
              </Box>

              <Box p={{ base: 6, md: 8, lg: 10 }} flex="1" minW={0}>
                <Heading
                  as="h2"
                  color="white"
                  fontSize={{ base: "xl", md: "2xl" }}
                >
                  {club.title}
                </Heading>
                <Text
                  mt="4"
                  maxW="58ch"
                  color="gray.300"
                  fontSize="md"
                  lineHeight="1.7"
                >
                  {club.description}
                </Text>
                {club.memberCount > 0 && (
                  <Text mt="4" color="gray.400" fontSize="sm">
                    {club.memberCount}{" "}
                    {club.memberCount === 1 ? "member" : "members"} so far
                  </Text>
                )}
              </Box>
            </Flex>
          </Box>

          <Box mt={{ base: 6, md: 8 }}>
            <JoinCta authData={authData} joined={club.joined} />
          </Box>
        </Container>
      </Box>

      <Box as="section" bg="#0B0D19" py={{ base: 12, md: 16 }}>
        <Container maxW="container.lg">
          <Heading
            as="h2"
            color="white"
            textAlign="center"
            fontSize={{ base: "2xl", md: "3xl" }}
            mb={{ base: 8, md: 10 }}
          >
            How the Book Club works
          </Heading>
          <Flex
            direction={{ base: "column", md: "row" }}
            gap={{ base: 6, md: 8 }}
          >
            {[
              {
                title: "One reading order, together",
                body: "Everyone reads the same curated reading order, added straight to your Redwing reading list when you join.",
              },
              {
                title: "Track your progress",
                body: "Tick issues off as you read and see how the rest of the club is getting on.",
              },
              {
                title: "Spoiler-safe discussions",
                body: "Discussion threads unlock as the club reaches them, so nothing gets spoiled before you get there.",
              },
            ].map((item) => (
              <Box
                key={item.title}
                flex="1"
                bg="gray.700"
                borderWidth="1px"
                borderColor="whiteAlpha.200"
                borderRadius="xl"
                p={{ base: 6, md: 7 }}
              >
                <Heading as="h3" color="white" fontSize="lg">
                  {item.title}
                </Heading>
                <Text mt="3" color="gray.300" lineHeight="1.7">
                  {item.body}
                </Text>
              </Box>
            ))}
          </Flex>
          <Flex mt={{ base: 8, md: 10 }} justify="center">
            <JoinCta authData={authData} joined={club.joined} />
          </Flex>
        </Container>
      </Box>
    </PageShell>
  );
};

export default PublicBookClub;
